import { MapPin, Handshake, MessagesSquare, Factory } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/site/Reveal";

const LOCAL_IMAGE = "/region-luedenscheid.webp";

const CITIES = [
  "Lüdenscheid",
  "Hagen",
  "Iserlohn",
  "Altena",
  "Plettenberg",
  "Halver",
  "Meinerzhagen",
  "Werdohl",
  "Kierspe",
];

const USPS = [
  {
    icon: Handshake,
    title: "Persönlich vor Ort",
    text: "Kein anonymes Ticketsystem — wir treffen uns auf einen Kaffee in Lüdenscheid oder bei Ihnen im Betrieb.",
  },
  {
    icon: MessagesSquare,
    title: "Kurze Wege, schnelle Antworten",
    text: "Direkter Draht zu Ihrem Ansprechpartner. Anfragen beantworten wir in der Regel noch am selben Tag.",
  },
  {
    icon: Factory,
    title: "Mittelstand verstehen wir",
    text: "Vom Handwerksbetrieb bis zum Industriezulieferer: Wir kennen die Anforderungen regionaler KMU im Märkischen Kreis.",
  },
];

export const LocalSeo = () => {
  return (
    <section id="ueber-uns" data-testid="local-section" className="relative border-t border-white/10 bg-[#0A0A0A] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          {/* Copy */}
          <div>
            <Reveal>
              <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-[#C5A059]">
                ÜBER UNS · REGIONAL VERWURZELT
              </p>
              <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl">
                Webdesign & Digitalpartner für Lüdenscheid & den Märkischen Kreis
              </h2>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">
                WANGO MEDIA verbindet ästhetisches High-End Webdesign mit der persönlichen Nähe und Verlässlichkeit eines starken regionalen Partners. Wir helfen Unternehmen im Sauerland, sich überzeugend und modern im Netz zu präsentieren.
              </p>
            </Reveal>

            <div className="mt-10 space-y-6">
              {USPS.map((usp, i) => (
                <Reveal key={usp.title} delay={i * 100}>
                  <div className="flex gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-[#121212] text-[#C5A059]">
                      <usp.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-white">{usp.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{usp.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={300}>
              <div className="mt-10 flex flex-wrap gap-2" data-testid="city-chips">
                {CITIES.map((city) => (
                  <Badge
                    key={city}
                    variant="outline"
                    className="rounded-full border-white/10 bg-[#121212] px-3.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors duration-200 hover:border-[#C5A059]/50 hover:text-[#C5A059]"
                  >
                    {city}
                  </Badge>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Image */}
          <Reveal delay={150}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl border border-[#C5A059]/20" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-2xl border border-white/10">
                <img
                  src={LOCAL_IMAGE}
                  alt="Luftaufnahme einer Stadt im Sauerland bei Abenddämmerung - Region Lüdenscheid und Märkischer Kreis"
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="600"
                  className="aspect-[4/3] w-full object-cover grayscale opacity-90 hover:grayscale-0 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-80" />
              </div>
              <div className="absolute -bottom-5 left-5 flex items-center gap-3 rounded-xl border border-white/10 bg-[#121212] px-5 py-3.5 shadow-2xl">
                <MapPin className="h-5 w-5 text-[#C5A059]" />
                <div>
                  <p className="text-sm font-semibold text-white">Lüdenscheid, NRW</p>
                  <p className="text-xs text-muted-foreground">Märkischer Kreis & Sauerland</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
